import React, { useState, useEffect, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { Save, Download, Upload, Settings, Check, Loader2, Radio, Box, ChevronDown, FilePlus2, SaveAll, Share2, Edit3, User, Bell, Sun, Moon } from 'lucide-react';
import { useProjectStore } from '../../store/projectStore';
import { useAuthStore } from '../../store/authStore';
import { useCollectionStore } from '../../store/collectionStore';
import {
  useStockNotificationsStore,
  isJobStockNotification,
  StockNotification,
} from '../../store/stockNotificationsStore';

function navigateFromNotification(n: StockNotification) {
  if (n.jobId && isJobStockNotification(n.type)) {
    window.dispatchEvent(new CustomEvent('openJobFromNotification', {
      detail: { siteId: n.siteId ?? undefined, jobId: n.jobId },
    }));
    return;
  }
  if (n.requestId) {
    window.dispatchEvent(new CustomEvent('openStockRequest', {
      detail: { requestId: n.requestId },
    }));
  }
}

function formatNotifTime(d: Date): string {
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
    + ' · '
    + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

const GlobalNotificationsBell: React.FC = () => {
  const { user } = useAuthStore();
  const { notifications, unreadCount, fetchNotifications, markRead, markAllRead } = useStockNotificationsStore();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!user) return;
    void fetchNotifications();
  }, [user, fetchNotifications]);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [open]);

  if (!user) return null;

  const handleClick = async (n: StockNotification) => {
    if (!n.read) await markRead(n.id);
    setOpen(false);
    navigateFromNotification(n);
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => {
          setOpen(v => {
            if (!v) void fetchNotifications();
            return !v;
          });
        }}
        className="relative h-8 w-8 flex items-center justify-center bg-gray-600 hover:bg-gray-700 rounded-md transition-colors"
        title="Notifications"
        aria-label="Notifications"
        aria-expanded={open}
      >
        <Bell size={16} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-xs font-semibold flex items-center justify-center leading-none">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-96 max-w-[calc(100vw-2rem)] rounded-lg shadow-2xl border border-slate-700 bg-slate-900 overflow-hidden z-[150]">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700">
            <span className="text-sm font-semibold text-white">Notifications</span>
            {notifications.some(n => !n.read) && (
              <button
                type="button"
                onClick={() => void markAllRead()}
                className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-[min(24rem,70vh)] overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-slate-400">
                No notifications yet
              </div>
            ) : (
              notifications.map(n => (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => void handleClick(n)}
                  className={`w-full text-left px-4 py-3 border-b border-slate-800 transition-colors hover:bg-slate-800/80 ${
                    n.read ? 'bg-transparent' : 'bg-slate-800/40'
                  }`}
                >
                  <p className="text-sm leading-relaxed text-slate-100">{n.message}</p>
                  <p className="text-sm text-slate-400 mt-1">{formatNotifTime(n.createdAt)}</p>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const Header: React.FC = () => {
  const { 
    currentProject,
    availableProjects,
    lastSaved, 
    isAutoSaving, 
    saveProject,
    createProject,
    loadProject,
    loadLastProject,
    refreshAvailableProjects,
    renameProject,
    setSettingsConfigOpen,
    showAPRange,
    setShowAPRange,
    autosaveSettings,
    getCurrentDesign,
    appTheme,
    setAppTheme,
  } = useProjectStore(
    useShallow((s) => ({
      currentProject: s.currentProject,
      availableProjects: s.availableProjects,
      lastSaved: s.lastSaved,
      isAutoSaving: s.isAutoSaving,
      saveProject: s.saveProject,
      createProject: s.createProject,
      loadProject: s.loadProject,
      loadLastProject: s.loadLastProject,
      refreshAvailableProjects: s.refreshAvailableProjects,
      renameProject: s.renameProject,
      setSettingsConfigOpen: s.setSettingsConfigOpen,
      showAPRange: s.showAPRange,
      setShowAPRange: s.setShowAPRange,
      autosaveSettings: s.autosaveSettings,
      getCurrentDesign: s.getCurrentDesign,
      appTheme: s.appTheme,
      setAppTheme: s.setAppTheme,
    }))
  );

  const { createBlankCollection, isLoading: collectionLoading } = useCollectionStore();
  
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newCustomer, setNewCustomer] = useState('');
  const [newAddress, setNewAddress] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renameName, setRenameName] = useState('');
  const [confirmOpenDialog, setConfirmOpenDialog] = useState(false);
  const [pendingOpenId, setPendingOpenId] = useState<string | null>(null);
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  const [isThemeMenuOpen, setIsThemeMenuOpen] = useState(false);
  const themeMenuRef = useRef<HTMLDivElement>(null);
  const { profile, user, signOut, isLoading: authLoading } = useAuthStore();

  const displayName = profile?.displayName ?? user?.email?.split('@')[0] ?? 'User';
  const displayInitial = displayName.charAt(0).toUpperCase();
  // Load last local-only project when not signed in
  React.useEffect(() => {
    if (user) return;
    loadLastProject();
    refreshAvailableProjects();
  }, [user, loadLastProject, refreshAvailableProjects]);

  React.useEffect(() => {
    if (!isThemeMenuOpen) return;
    const onMouseDown = (e: MouseEvent) => {
      if (themeMenuRef.current && !themeMenuRef.current.contains(e.target as Node)) {
        setIsThemeMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [isThemeMenuOpen]);


  const handleManualSave = () => {
    if (currentProject) {
      saveProject();
    }
  };

  const handleNewDesign = () => {
    setNewName('');
    setNewCustomer('');
    setNewAddress('');
    setNewDialogOpen(true);
  };

  const handleOpenDesign = (id: string) => {
    // Always confirm before opening another design
    setPendingOpenId(id);
    setConfirmOpenDialog(true);
  };

  const handleRename = () => {
    if (!currentProject) return;
    setRenameName(currentProject.name || 'Untitled Collection');
    setRenameDialogOpen(true);
  };

  // Expose handleNewDesign for use in DesignListSidebar
  React.useEffect(() => {
    (window as any).handleNewDesign = handleNewDesign;
    return () => {
      delete (window as any).handleNewDesign;
  };
  }, []);

  const formatLastSaved = () => {
    if (!lastSaved) return '';
    const now = new Date();
    const diff = now.getTime() - lastSaved.getTime();
    const minutes = Math.floor(diff / 60000);
    
    // Show actual time
    const timeString = lastSaved.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    
    if (minutes === 0) return `Saved just now (${timeString})`;
    if (minutes === 1) return `Saved 1 minute ago (${timeString})`;
    if (minutes < 60) return `Saved ${minutes} minutes ago (${timeString})`;
    
    return `Saved at ${timeString}`;
  };

  const currentDesign = getCurrentDesign();

  return (
    <>
      <header className="bg-slate-900 text-white px-4 py-1.5 flex items-center justify-between shadow-lg">
        <div className="flex items-center space-x-4">
          <div className="flex items-baseline space-x-2">
            <h1 className="text-lg font-bold text-blue-400">NetBuilder</h1>
            {currentProject ? (
              <button
                onClick={() => window.dispatchEvent(new CustomEvent('openProjectList'))}
                className="px-2 py-1 rounded border border-slate-600 bg-slate-800 hover:bg-slate-700 font-medium text-white transition-colors cursor-pointer text-sm leading-none flex items-center gap-1.5 relative top-0.5"
              >
                <Box size={14} className="flex-shrink-0" />
                <span className="relative -top-px">{currentProject.name || 'Untitled Project'}</span>
              </button>
            ) : (
              <button
                onClick={handleNewDesign}
                className="px-2 py-1 rounded border border-slate-600 bg-slate-800 hover:bg-slate-700 font-medium text-white transition-colors cursor-pointer text-sm leading-none flex items-center gap-1.5 relative top-0.5"
              >
                <FilePlus2 size={14} className="flex-shrink-0" />
                <span className="relative -top-px">New Collection</span>
              </button>
            )}
          </div>
          {!currentProject && (
            <div className="text-xs text-gray-400">No collection loaded</div>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          {currentProject && (
            <div className="flex items-center space-x-2 text-xs mr-1">
              {isAutoSaving ? (
                <>
                  <Loader2 size={12} className="animate-spin text-blue-400" />
                  <span className="text-blue-400 whitespace-nowrap">Saving...</span>
                </>
              ) : lastSaved ? (
                <>
                  <Check size={12} className="text-green-400 flex-shrink-0" />
                  <span className="text-green-400 whitespace-nowrap">{formatLastSaved()}</span>
                </>
              ) : null}

              {autosaveSettings.enabled ? (
                <div className="flex items-center space-x-1 ml-1 pl-2 border-l border-slate-600">
                  <span className="w-1.5 h-1.5 bg-green-400 rounded-full" />
                  <span className="text-green-400 whitespace-nowrap">Auto</span>
                </div>
              ) : (
                <div className="flex items-center space-x-1 ml-1 pl-2 border-l border-slate-600">
                  <span className="w-1.5 h-1.5 bg-gray-500 rounded-full" />
                  <span className="text-gray-500 whitespace-nowrap">Manual</span>
                </div>
              )}
            </div>
          )}

          {/* Temporary Save Button for Testing */}
          {currentProject && (
            <button
              onClick={handleManualSave}
              disabled={isAutoSaving}
              className="h-8 w-8 flex items-center justify-center bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-md transition-colors shrink-0"
              title="Save Collection"
              aria-label="Save collection"
            >
              {isAutoSaving ? (
                <Loader2 size={16} className="animate-spin" aria-hidden />
              ) : (
                <Save size={16} aria-hidden />
              )}
            </button>
          )}

          <div className="relative" ref={themeMenuRef}>
            <button
              type="button"
              onClick={() => setIsThemeMenuOpen((open) => !open)}
              className="h-8 px-2 flex items-center justify-center gap-0.5 bg-gray-600 hover:bg-gray-700 rounded-md transition-colors shrink-0"
              title="Theme"
              aria-label="Theme"
              aria-expanded={isThemeMenuOpen}
              aria-haspopup="listbox"
            >
              {appTheme === 'dark' ? <Moon size={14} aria-hidden /> : <Sun size={14} aria-hidden />}
              <ChevronDown size={12} aria-hidden className="opacity-80" />
            </button>
            {isThemeMenuOpen && (
              <div className="absolute right-0 top-full mt-1.5 w-32 rounded-md shadow-2xl border border-slate-700 bg-slate-900 overflow-hidden z-[150]">
                <button
                  type="button"
                  onClick={() => {
                    setAppTheme('default');
                    setIsThemeMenuOpen(false);
                  }}
                  className={`w-full flex items-center gap-2 px-2.5 py-1.5 text-xs text-left transition-colors ${
                    appTheme !== 'dark' ? 'bg-slate-800 text-white' : 'text-slate-200 hover:bg-slate-800'
                  }`}
                >
                  <Sun size={13} />
                  Light
                  {appTheme !== 'dark' && <Check size={12} className="ml-auto text-green-400" />}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setAppTheme('dark');
                    setIsThemeMenuOpen(false);
                  }}
                  className={`w-full flex items-center gap-2 px-2.5 py-1.5 text-xs text-left transition-colors ${
                    appTheme === 'dark' ? 'bg-slate-800 text-white' : 'text-slate-200 hover:bg-slate-800'
                  }`}
                >
                  <Moon size={13} />
                  Dark
                  {appTheme === 'dark' && <Check size={12} className="ml-auto text-green-400" />}
                </button>
              </div>
            )}
          </div>

          {/* Settings */}
          <button 
            onClick={() => setSettingsConfigOpen(true)}
            className="h-8 w-8 flex items-center justify-center bg-gray-600 hover:bg-gray-700 rounded-md transition-colors"
            title="Open Settings"
            aria-label="Open Settings"
          >
            <Settings size={16} />
          </button>

          <GlobalNotificationsBell />

          {/* Account */}
          <button
            onClick={() => setIsAccountMenuOpen(prev => !prev)}
            className="h-8 w-8 flex items-center justify-center bg-gray-600 hover:bg-gray-500 rounded-full transition-colors"
            title="Account"
            aria-label="Account"
          >
            <User size={16} />
          </button>
        </div>
      </header>

      {/* Simple local account menu (placeholder for future auth) */}
      {isAccountMenuOpen && (
        <div
          className="fixed top-12 right-4 z-[140] bg-slate-900 text-white rounded-lg shadow-2xl border border-slate-700 w-64"
        >
          <div className="px-4 py-3 border-b border-slate-700 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center text-sm font-semibold">
                <span>{displayInitial}</span>
              </div>
              <div>
                <div className="text-sm font-semibold">{displayName}</div>
                <div className="text-[11px] text-slate-400 truncate max-w-[140px]">{user?.email ?? ''}</div>
              </div>
            </div>
            <button
              className="p-1 rounded hover:bg-slate-800"
              onClick={() => setIsAccountMenuOpen(false)}
            >
              ✕
            </button>
          </div>
          <div className="px-4 py-3">
            <button
              onClick={async () => { await signOut(); setIsAccountMenuOpen(false); }}
              disabled={authLoading}
              className="w-full text-xs px-3 py-2 rounded bg-slate-700 hover:bg-slate-600 transition-colors disabled:opacity-50"
            >
              Sign out
            </button>
          </div>
        </div>
      )}

      {/* Confirm Open Design dialog */}
  {confirmOpenDialog && (
    <div className="fixed inset-0 z-[130] bg-black/30 flex items-center justify-center" onClick={() => setConfirmOpenDialog(false)}>
      <div className="bg-slate-900 text-white rounded-lg shadow-2xl w-[480px] max-w-[92vw] border border-slate-700" onClick={(e)=>e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-slate-700 font-semibold text-sm">Open Collection</div>
        <div className="p-4 text-sm text-slate-300">
          You have an open collection. Do you want to save before opening another collection?
        </div>
        <div className="px-4 py-3 border-t border-slate-700 bg-slate-900 flex justify-end gap-2">
          <button className="px-3 py-1.5 text-xs rounded bg-slate-700 hover:bg-slate-600" onClick={() => setConfirmOpenDialog(false)}>Cancel</button>
          <button
            className="px-3 py-1.5 text-xs rounded bg-orange-600 hover:bg-orange-700"
            onClick={() => {
              // Discard changes, just open
              if (pendingOpenId) {
                loadProject(pendingOpenId);
              }
              setConfirmOpenDialog(false);
              setPendingOpenId(null);
            }}
          >Discard</button>
          <button
            className="px-3 py-1.5 text-xs rounded bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            disabled={!currentProject}
            onClick={() => {
              if (currentProject) {
                saveProject();
              }
              if (pendingOpenId) {
                loadProject(pendingOpenId);
              }
              setConfirmOpenDialog(false);
              setPendingOpenId(null);
            }}
          >Save</button>
        </div>
      </div>
    </div>
  )}

      {/* New Collection Dialog */}
      {newDialogOpen && (
        <div className="fixed inset-0 z-[120] bg-black/30 flex items-center justify-center" onClick={() => setNewDialogOpen(false)}>
          <div className="bg-slate-900 text-white rounded-lg shadow-2xl w-[520px] max-w-[92vw] border border-slate-700" onClick={(e)=>e.stopPropagation()}>
            <div className="px-4 py-3 border-b border-slate-700 font-semibold text-sm flex items-center justify-between">
              <span>New Collection</span>
              <button className="p-1 hover:bg-slate-800 rounded" onClick={() => setNewDialogOpen(false)}>✕</button>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="block text-xs text-slate-400 mb-1">Collection Name</label>
                <input value={newName} onChange={e=>setNewName(e.target.value)} className="w-full px-3 py-2 rounded bg-slate-800 border border-slate-700 text-sm" placeholder="New Collection"/>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Customer Name</label>
                  <input value={newCustomer} onChange={e=>setNewCustomer(e.target.value)} className="w-full px-3 py-2 rounded bg-slate-800 border border-slate-700 text-sm" placeholder="Customer"/>
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Address</label>
                  <input value={newAddress} onChange={e=>setNewAddress(e.target.value)} className="w-full px-3 py-2 rounded bg-slate-800 border border-slate-700 text-sm" placeholder="Address"/>
                </div>
              </div>
            </div>
            <div className="px-4 py-3 border-t border-slate-700 bg-slate-900 flex justify-end gap-2">
              <button className="px-3 py-1.5 text-xs rounded bg-slate-700 hover:bg-slate-600" onClick={() => setNewDialogOpen(false)}>Cancel</button>
              <button
                className="px-3 py-1.5 text-xs rounded bg-blue-600 hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1.5"
                disabled={!newName.trim() || isCreating}
                onClick={async () => {
                  setIsCreating(true);
                  try {
                    if (user) {
                      await createBlankCollection(newName.trim(), newCustomer.trim(), newAddress.trim());
                    } else {
                      createProject(newName.trim(), newCustomer.trim(), newAddress.trim());
                    }
                  } finally {
                    setIsCreating(false);
                    setNewDialogOpen(false);
                  }
                }}
              >
                {isCreating && <Loader2 size={12} className="animate-spin" />}
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rename Collection Dialog */}
      {renameDialogOpen && (
        <div className="fixed inset-0 z-[120] bg-black/30 flex items-center justify-center" onClick={() => setRenameDialogOpen(false)}>
          <div className="bg-slate-900 text-white rounded-lg shadow-2xl w-[520px] max-w-[92vw] border border-slate-700" onClick={(e)=>e.stopPropagation()}>
            <div className="px-4 py-3 border-b border-slate-700 font-semibold text-sm flex items-center justify-between">
              <span>Rename Collection</span>
              <button className="p-1 hover:bg-slate-800 rounded" onClick={() => setRenameDialogOpen(false)}>✕</button>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="block text-xs text-slate-400 mb-1">Collection Name</label>
                <input
                  value={renameName}
                  onChange={e => setRenameName(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && renameName.trim() && currentProject) {
                      renameProject(currentProject.id, renameName.trim());
                      setRenameDialogOpen(false);
                    }
                  }}
                  className="w-full px-3 py-2 rounded bg-slate-800 border border-slate-700 text-sm"
                  autoFocus
                />
              </div>
            </div>
            <div className="px-4 py-3 border-t border-slate-700 bg-slate-900 flex justify-end gap-2">
              <button className="px-3 py-1.5 text-xs rounded bg-slate-700 hover:bg-slate-600" onClick={() => setRenameDialogOpen(false)}>Cancel</button>
              <button
                className="px-3 py-1.5 text-xs rounded bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                disabled={!renameName.trim() || !currentProject}
                onClick={() => {
                  if (!currentProject) return;
                  renameProject(currentProject.id, renameName.trim());
                  setRenameDialogOpen(false);
                }}
              >Save</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
